import { lazyRouteComponent } from '@tanstack/react-router';
import { isChunkLoadError } from './chunk-reload';

/* Страница админки, которая догружается при первом переходе на неё, — с повтором загрузки.

   Safari (WebKit) запоминает адрес модуля, который однажды не скачался, за вкладкой: повторный
   `import()` того же адреса падает сразу, без запроса к серверу, — и после ↻ тоже, пока вкладку
   не закрыть (проверено в WebKit 26.4, 27.09.2026). Сам файл при этом на месте: `fetch` его отдаёт,
   а `import` того же файла с меткой в адресе загружается. Поэтому при сбое повторяем по адресу
   с меткой — для WebKit это другой модуль, и он честно идёт за ним на сервер. */

/** То же, что `lazyRouteComponent`, но с повтором загрузки по адресу с меткой. */
export const lazyPage: typeof lazyRouteComponent = (importer, exportName) =>
  lazyRouteComponent(() => importWithRetry(importer), exportName);

export async function importWithRetry<T>(
  importer: () => Promise<T>,
  importUrl: (url: string) => Promise<unknown> = (url) => import(/* @vite-ignore */ url),
): Promise<T> {
  try {
    return await importer();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;
    const url = chunkUrlOf(importer);
    if (!url) throw error;
    try {
      return (await importUrl(`${url}?retry=${Date.now()}`)) as T;
    } catch {
      throw error;
    }
  }
}

/** Адрес файла страницы — из текста самой функции загрузки. После сборки это
 *  `()=>__vitePreload(()=>import("./RolesPage-DGKcFnLU.js"),…)`: путь относителен каталога
 *  файлов сборки, где лежит и этот модуль, поэтому разрешаем его от `import.meta.url`. */
export function chunkUrlOf(importer: () => unknown, base: string = import.meta.url): string | null {
  const match = /import\(\s*["'`]([^"'`]+\.js)["'`]\s*\)/.exec(String(importer));
  if (!match?.[1]) return null;
  try {
    return new URL(match[1], base).href;
  } catch {
    return null;
  }
}
